/**
 * Hook для проверки доступа к функциям по подписке
 * 
 * Usage:
 *   const { hasAccess, requiredTier, daysLeft, isLoading } = useSubscriptionAccess('monthly');
 * 
 * Особенности:
 * - Кэширует результат на 5 минут
 * - Автоматически обновляет при изменении
 * - Возвращает метаданные (daysLeft, endDate, reason)
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  SubscriptionTier,
  AccessCheckResult,
  TIER_ORDER,
  TIER_LABELS,
} from '../../shared/types/access';

const API_URL = import.meta.env.VITE_API_URL || '';

// Кэш результатов (в памяти)
const accessCache = new Map<string, { result: AccessCheckResult; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 минут

interface UseSubscriptionAccessResult extends AccessCheckResult {
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSubscriptionAccess(featureId: string): UseSubscriptionAccessResult {
  const { user, isAuthenticated } = useAuth();
  
  const [result, setResult] = useState<AccessCheckResult>({
    hasAccess: false,
    requiredTier: 'free',
    userTier: 'free',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAccess = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setResult({
        hasAccess: false,
        requiredTier: 'free',
        userTier: 'free',
        reason: 'no_subscription',
      });
      setIsLoading(false);
      return;
    }

    // Проверяем кэш
    const cacheKey = `${featureId}-${user.id}`;
    const cached = accessCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setResult(cached.result);
      setIsLoading(false);
      return;
    }

    // Загружаем с сервера
    try {
      setIsLoading(true);
      setError(null);

      const res = await fetch(`${API_URL}/api/payment/access/${featureId}`, {
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      
      if (data.success) {
        const accessResult = data.data as AccessCheckResult;
        
        // Сохраняем в кэш
        accessCache.set(cacheKey, {
          result: accessResult,
          timestamp: Date.now(),
        });

        setResult(accessResult);
      } else {
        throw new Error(data.message || 'Ошибка проверки доступа');
      }
    } catch (err: any) {
      console.error(`[useSubscriptionAccess] Error checking ${featureId}:`, err);
      setError(err.message || 'Ошибка проверки доступа');
      
      // Fallback: считаем что доступа нет
      setResult({
        hasAccess: false,
        requiredTier: 'free',
        userTier: 'free',
        reason: 'no_subscription',
      });
    } finally {
      setIsLoading(false);
    }
  }, [featureId, user, isAuthenticated]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const refetch = useCallback(() => {
    // Очищаем кэш для этого featureId
    const cacheKey = `${featureId}-${user?.id}`;
    accessCache.delete(cacheKey);
    checkAccess();
  }, [featureId, user, checkAccess]);

  return {
    ...result,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Хук для получения статуса подписки пользователя
 */
export function useSubscriptionStatus() {
  const { user, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState<{
    tier: SubscriptionTier;
    billingPeriod: 'monthly' | 'yearly';
    endDate: string;
    isActive: boolean;
    daysLeft: number;
    autoRenew: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const res = await fetch(`${API_URL}/api/payment/subscription-info`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const data = await res.json();
        
        if (data.success) {
          setStatus(data.data);
        } else {
          throw new Error(data.message || 'Ошибка получения статуса');
        }
      } catch (err: any) {
        console.error('[useSubscriptionStatus] Error:', err);
        setError(err.message || 'Ошибка получения статуса');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [user, isAuthenticated]);

  const refetch = useCallback(() => {
    fetchStatus();
  }, [user, isAuthenticated]);

  return { status, isLoading, error, refetch };
}

/**
 * Утилита для проверки уровня тарифа
 */
export function canAccessTier(userTier: string, requiredTier: string): boolean {
  const userLevel = TIER_ORDER[userTier as keyof typeof TIER_ORDER] ?? 0;
  const requiredLevel = TIER_ORDER[requiredTier as keyof typeof TIER_ORDER] ?? 0;
  return userLevel >= requiredLevel;
}

/**
 * Получить цвет бейджа для тарифа
 */
export function getTierBadgeColor(tier: string): string {
  return TIER_ORDER[tier as keyof typeof TIER_ORDER] !== undefined
    ? { free: '#9ca3af', base: '#4fd1c5', premium: '#d4af37', pro: '#9B59B6' }[tier as keyof typeof TIER_ORDER] || '#9ca3af'
    : '#9ca3af';
}

/**
 * Получить label для тарифа
 */
export function getTierLabel(tier: string): string {
  return TIER_LABELS[tier as keyof typeof TIER_LABELS] || tier;
}
